<template>
  <div class="campaign-show-section">
    <div class="campaign-show-header">
      <svg class="primary-icon campaign-indicator-section" viewBox="300 -1500 1000 3000" v-if="noPrimary">
        <use xlink:href="#primary-icon"></use>
      </svg>
      <svg class="secondary-icon campaign-indicator-section" viewBox="300 -2000 1000 3000" v-if="backupIds === undefined">
        <use xlink:href="#secondary-icon"></use>
      </svg>
      <h1 class="page-header text-center">campaign #{{campaignId}}</h1>
    </div>

    <div v-if="errorMessage" class="error-label text-center">{{errorMessage}}</div>

    <div v-if="reviewStatus == 'pending'" class="alert_bubble_dashboard campaign-pending large-8">
      <ul id="pendingStatusBar">
        <li class="completed">submitted</li>
        <li :class="reviewStatusComplete ? 'completed' : 'current'">
          <img :src="sb_logo" />
          <p>received</p>
        </li>
        <li :class="reviewStatusComplete ? 'current' : ''">
          <img :src="sb_logo" />
          <p>reviewing</p>
        </li>
        <li>sending</li>
      </ul>
      <div class="pendingStatusContainer">
        <h3>{{pendingHeader}}</h3>
        <p>{{pendingText}}</p>
        <div id="loadingIcon">
          <embed :src="loadingImage" />
        </div>
        <div id="amDetailsCancel">
          <div class="cancelContainer">
              <a v-if="hasAccess(userAbilitiesValue, 'CAMPAIGNS_CREATE') && communicationDetails"
                 class="cancel_pending"
                 data-confirm="are you sure you want to stop editing this campaign?"
                 rel="nofollow"
                 data-method="delete"
                 :href="`/campaigns/${communicationDetails.id}`">cancel campaign</a>
          </div>
          <div class="accountManagerContainer" v-if="accountManager">
            <div class="imageNameWrapper">
              <img v-if="accountManager.display_picture" :src="accountManager.display_picture">
              <i v-else :src="accountManager.display_picture" class="flaticon-people"></i>
            </div>
            <div class="contactWrapper">
              <a v-if="accountManager.phone_number" :href="'tel:'+accountManager.phone_number">call {{accountManager.name.substr(0, accountManager.name.indexOf(' '))}}</a>
              <h3 v-if="accountManager.phone_number">{{accountManager.phone_number}}</h3>
              <p>{{accountManager.email}}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="reviewStatus == 'rejected'" class="alert_bubble_dashboard campaign-rejected large-8">
      <h3>unfortunately we need to rework this campaign</h3>
      <p><strong>issue:</strong> {{rejectedIssue}}</p>
      <p><strong>suggested change:</strong> {{suggestedChange}}</p>
      <br>
      <p>please change the above issues and try again or reach out to your client success manager to get some help</p>
      <a :class="`button larger resend-campaign-link`" :href="`/campaigns/${campaignId}/clone_campaign`">rework campaign</a>
      <div id="amDetailsCancel">
        <div class="accountManagerContainer" v-if="accountManager">
          <div class="imageNameWrapper">
            <img v-if="accountManager.display_picture" :src="accountManager.display_picture">
            <i v-else :src="accountManager.display_picture" class="flaticon-people"></i>
          </div>
          <div class="contactWrapper">
            <a v-if="accountManager.phone_number" :href="'tel:'+accountManager.phone_number">call {{accountManager.name.substr(0, accountManager.name.indexOf(' '))}}</a>
            <h3 v-if="accountManager.phone_number">{{accountManager.phone_number}}</h3>
            <p>{{accountManager.email}}</p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="reviewStatus == 'approved' && communicationDetails && !communicationDetails.time_sent" class="alert_bubble_dashboard campaign-approved large-8">
      <div class="pendingStatusContainer">
        <h3>campaign approved!</h3>
        <div v-if="communicationDetails.send_at">
          your campaign is scheduled to go out on {{formattedSendAt}}
        </div>
        <div v-else>
          your campaign will be sent out within the next 5 minutes
        </div>
        <div id="confirmedIcon">
          <i class="flaticon-checkmark-for-verification" />
        </div>
      </div>
    </div>

    <div v-if="communicationDetails && communicationDetails.time_sent" class="row">
      <div class="large-12 columns small-centered">
        <stats-widget
            :extensionName="extension.name"
            :isTracked="isTracked"
            :isEmail="emailSend"
            :isSms="smsSend"
            :isPush="pushSend"
            :rewardType="rewardType"
            :url="`/api/v3/campaigns/${campaignId}/stats`"
            :key="`stats-widget-${trackingDuration}`"
            :trackingDuration.sync="trackingDuration"
            @setCreditsUsed="setCreditsUsed">
          <slot>
            <div class="m-6">
              <div class="text-center">
                tracking duration post customer reception of campaign
              </div>
              <div class="duration-container flex flex-row content-center justify-center">
                <template v-for="duration in trackingDurationList">
                  <input
                    v-model="trackingDuration"
                    class="tracking-duration-radio"
                    type="radio"
                    :value="duration"
                    :name="'duration-' + duration"
                    :id="'duration-' + duration"/>
                  <label
                    class="tracking-duration-container"
                    :key="`radio-${duration}`"
                    :for="'duration-' + duration">
                    {{ duration }}
                  </label>
                </template>
              </div>
            </div>
          </slot>
        </stats-widget>

        <div v-if="isOffer" class="divider"></div>

        <extension-details
            v-if="extension.name !== '--'"
            :extension="extension"
            :extensionType="extensionType"
            :isOffer="isOffer"
            :rewardId="rewardId"
            @updateRewardType="updateRewardType">
        </extension-details>

        <report-graphs
          :campaignId="campaignId"
          :trackingDuration="trackingDuration"
          :key="`report-graph-${trackingDuration}`">
        </report-graphs>
      </div>
    </div>

    <div class="row">
      <div class="large-8 columns small-centered">
        <campaign-details
            @primarySecondary="primarySecondary"
            :campaignItem="communicationDetails"
            :creditsUsed="creditsUsed"
            :reviewStatus="reviewStatus">
        </campaign-details>

        <selected-groups
            v-if="selectedGroupsUrl"
            :campaignId="campaignId"
            :selectedGroupsUrl="selectedGroupsUrl">
        </selected-groups>

        <content-section
          v-if="communicationDetails"
          :campaignItem="communicationDetails"
          :isTracked="isTracked">
        </content-section>
      </div>
    </div>

    <div v-if="hasAccess(userAbilitiesValue, 'CAMPAIGNS_CREATE') && !classicCampaignsEnabled" class="row">
      <div class="large-12 columns center-button">
        <a :class="`button larger resend-campaign-link`" :href="`/campaigns/${campaignId}/clone_campaign`">resend campaign</a>
      </div>
    </div>
  </div>
</template>

<script>
  import AxiosMixin from '../../mixins/AxiosMixin'
  import CampaignDetails from '../../components/Campaigns/CampaignDetails'
  import CommunicationShowMixin from '../../mixins/CommunicationShowMixin'
  import ContentSection from '../../components/CommunicationItems/ContentSection'
  import ExtensionDetails from '../../components/CommunicationItems/ExtensionDetails'
  import { isObject, formatPhoneNumber } from '../../utilities/index'
  import LoadErrorMixin from '../../mixins/LoadErrorMixin'
  import { mapGetters, mapState } from 'vuex'
  import RemoveRailsLoadingMixin from '../../mixins/RemoveRailsLoadingMixin'
  import ReportGraphs from '../../components/CommunicationItems/ReportGraphs'
  import SelectedGroups from '../../components/GroupWidget/SelectedGroups'
  import StatsWidget from '../../components/CommunicationItems/StatsWidget'
  import UserAbilitiesMixin from '../../mixins/UserAbilitiesMixin'
  import moment from 'moment-timezone'
  import loadingImage from 'sb-loading.svg'
  import sb_logo from 'springbig_leaf_logo.png'
  import {trackingDurationList} from "../../config/tracking";


  export default {
    name: 'CampaignShow',

    components: {
      CampaignDetails,
      ContentSection,
      ExtensionDetails,
      ReportGraphs,
      SelectedGroups,
      StatsWidget,
    },

    mixins: [AxiosMixin, CommunicationShowMixin, LoadErrorMixin, RemoveRailsLoadingMixin, UserAbilitiesMixin],

    data() {
      return {
        accountManager: null,
        backupIds: null,
        campaignId: null,
        creditsUsed: 'calculating',
        emailSend: false,
        smsSend: false,
        pushSend: false,
        errorMessage: null,
        errorText: 'we encountered an issue loading the campaign details.',
        formattedSendAt: null,
        loadingIcon: null,
        moment,
        loadingImage,
        sb_logo,
        noPrimary: null,
        parentId: null,
        pendingHeader: "content review underway",
        pendingText: "your message is being analyzed by our content review team to ensure maximum deliverability - this may take a few minutes...",
        reviewStatus: null,
        rejectedIssue: null,
        reviewStatusChangedAt: null,
        reviewStatusComplete: null,
        suggestedChange: null,
        trackingDuration: '2d',
        trackingDurationList,
      }
    },
    methods: {
      primarySecondary(primary, secondary) {
        this.parentId = primary
        this.backupIds = secondary
        this.noPrimary = this.backupIds.length > 0
      },

      loadCampaign() {
        const url = `/api/v3/campaigns/${this.campaignId}/details`
        this.axiosMethod({url, onSuccess: this.setCampaignData, onError: this.setLoadError})
      },

      loadStatus() {
        const url = `/api/v3/campaigns/${this.campaignId}/review_status`;
        this.axiosMethod({url, onSuccess: this.setCampaignStatus, onError: this.setLoadError})
      },

      setCampaignData(data) {
        if (data && isObject(data)) {
          this.adImage = data.ad_image
          this.communicationDetails = data
          this.extensionType = data.extension_type
          this.isTracked = true; //[1, 2, 3, 4, 5, 6].includes(this.extensionType)
          this.rewardId = data.reward_id
          this.emailSend = data.is_email_campaign
          this.smsSend = data.is_sms_campaign
          this.pushSend = data.is_push_campaign
          this.extension = this.configureExtension()
          if (!data.time_sent) { this.setCreditsUsed() }
          this.errorMessage = null
          this.reviewStatus = data.review_status
          if (this.reviewStatus === "rejected") {
            this.rejectedIssue = data.rejection_reason;
            this.suggestedChange = data.suggested_changes
          }
          if (data.account_manager) {
            this.accountManager = data.account_manager;
            if (this.accountManager.phone_number) {
              this.accountManager.phone_number = formatPhoneNumber(this.accountManager.phone_number);
            }
          }
          if (data.send_at) {
            this.formattedSendAt = this.formatToMerchantTimeZone(data.send_at);
          }
          this.reviewStatusCompletedEval()

        } else {
          this.errorMessage = this.errorText
        }
      },

      setCreditsUsed(credits) {
        const cancelled = this.communicationDetails.cancelled_at,
          notScheduled = !this.communicationDetails.send_at,
          sent = this.communicationDetails.time_sent
        if (notScheduled || sent || cancelled) {
          let showZeroOrDefault = cancelled ? '--' : 0
          if (sent || cancelled) { this.creditsUsed = credits ? credits : showZeroOrDefault }
        }
      },

      setCampaignStatus(response){
        this.reviewStatus = response.review_status;
        this.reviewStatusChangedAt = response.reviewStatusChangedAt;
        if (this.reviewStatus === 'pending') {
          this.initTimer(this.loadStatus, 30000)
          this.reviewStatusCompletedEval()
        } else {
          this.loadCampaign();
        }
        this.initTimer(this.reviewStatusCompletedEval, 6000)
      },

      initTimer(method, time) {
        setTimeout(() => {
          method();
        }, time)
      },

      formatToMerchantTimeZone(timeToFormat) {
        if (timeToFormat && !!this.currentMerchant && !!moment.tz) {
          const utcTime = moment.utc(timeToFormat),
            momentTzDate = utcTime.tz(this.currentMerchant.merchant_time_zone),
            dateFormatted = momentTzDate ? momentTzDate.format('MM/DD/YY') : '--',
            timeFormatted = momentTzDate ? momentTzDate.format('h:mma') : '--'
          return `${dateFormatted} at ${timeFormatted}`
        }
      },
      reviewStatusCompletedEval() {
        return this.reviewStatusComplete = moment().unix() - moment(this.reviewStatusChangedAt).unix() > 6
      },
    },

    created() {
      this.$store.dispatch('LOAD_CURRENT_MERCHANT')
      this.$store.dispatch('LOAD_CURRENT_USER')
      this.campaignId = this.$route.params.id
      this.loadCampaign()
      this.loadStatus()
    },

    mounted() {
      this.removeLoading('campaign-show-loading-icon')
    },
    computed: {
      ...mapGetters([
        'userAbilitiesValue', 'classicCampaignsEnabled'
      ]),

      ...mapState([
        'currentMerchant'
      ]),

      isOffer: function () {
        return this.rewardId && [1, 5, 6].includes(this.extensionType)
      },

      selectedGroupsUrl: function () {
        const hasGroups = this.communicationDetails && this.communicationDetails.has_groups
        return hasGroups ? `/api/v3/campaigns/${this.campaignId}/selected_groups?legacy=true` : false
      },
    },
  }
</script>
